import { isGranted, UC_SERVICE_ID } from '../usercentrics';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
    js3q?: any;
  }
}

export const js3q = ({ dataid, container }) => ({
  video: undefined,
  scriptInterval: undefined,
  dialog: {
    show: false,
    consent: false,
    init: false,
  },

  openDialog() {
    this.dialog.show = true;
    this.dialog.init = true;

    if (!this.dialog.consent) return;

    if (this.video) {
      this.video.enableControls();
      this.video.play();
    } else {
      this.createPlayer();
    }
  },
  closeDialog() {
    this.dialog.show = false;
    if (this.video) {
      this.video.pause();
    }
  },

  /**
   * Creates the 3Q player. Usercentrics only unblocks the player script once
   * consent has been given, so `window.js3q` may still be missing right after
   * the user accepted — wait for it instead of silently doing nothing.
   */
  createPlayer() {
    if (this.video) return;
    if (this.scriptInterval) clearInterval(this.scriptInterval);

    if (window.js3q) {
      this.initPlayer();
      return;
    }

    this.scriptInterval = setInterval(() => {
      if (!window.js3q) return;
      clearInterval(this.scriptInterval);
      this.scriptInterval = undefined;
      this.initPlayer();
    }, 200);
  },

  initPlayer() {
    const videoInstance = new window.js3q({
      dataid,
      container,
      autoplay: true,
      controls: true,
    });
    this.video = videoInstance;
    this.video.on('media.playing', () => {
      this.trackEconda('playing');
    });
    this.video.on('media.paused', () => {
      const length = this.video.getVideoInfo().Length.toFixed();
      const currentTime = this.video.getCurrentTime().toFixed();
      if (length !== currentTime) this.trackEconda('paused');
    });
    this.video.on('media.complete', () => {
      this.trackEconda('stopped');
    });
  },

  /** Open Usercentrics UI and display 3Q-Player details. */
  moreInfo() {
    window.__ucCmp?.showServiceDetails(UC_SERVICE_ID.video3Q);
  },

  /** Accepts Usercentrics 3Q-Player. */
  async accept() {
    if (!window.__ucCmp) return;
    await window.__ucCmp.updateServicesConsents([
      { id: UC_SERVICE_ID.video3Q, consent: true },
    ]);
    await window.__ucCmp.saveConsents('EXPLICIT');
    this.setConsent(true);
  },

  setConsent(granted: boolean) {
    if (granted === this.dialog.consent) return;
    this.dialog.consent = granted;

    if (!granted) {
      if (this.scriptInterval) clearInterval(this.scriptInterval);
      this.scriptInterval = undefined;
      this.video = undefined;
      return;
    }

    // Consent was given while the dialog is already open — start the player.
    if (this.dialog.show) this.createPlayer();
  },

  /**
   * Reads the current consent from the CMP. `__ucCmp` is only available once
   * the Usercentrics loader has initialized.
   */
  async syncConsent() {
    if (!window.__ucCmp) {
      window.addEventListener('UC_UI_INITIALIZED', () => this.syncConsent(), {
        once: true,
      });
      return;
    }

    this.setConsent(
      isGranted(await window.__ucCmp.getConsentDetails(), UC_SERVICE_ID.video3Q)
    );
  },

  init() {
    // UC_CONSENT fires with the initial consent state and on every change.
    window.addEventListener('UC_CONSENT', (e: any) => {
      this.setConsent(isGranted(e.detail, UC_SERVICE_ID.video3Q));
    });

    this.syncConsent();
  },

  trackEconda(state: 'playing' | 'paused' | 'stopped') {
    const videoInfo = this.video.getVideoInfo();
    const currentTime = this.video.getCurrentTime();
    if (!window.dataLayer) return;
    window.dataLayer.push({
      event: 'RichMedia',
      event_name: 'rich_media',
      content: {
        name: videoInfo.Metadata.Title,
        category: 'Sonstiges Video',
      },
      type: 'video',
      duration: videoInfo.Length,
      previewUri: dataid,
      state: [state, currentTime],
    });
  },
});
